import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from '../ui/Checkbox';
import Skeleton from '../ui/Skeleton';
import { cx } from '../ui/classNames';
import { SELECT_COLUMN_ID } from './columnLayout';
import { createSkeletonWidths } from './skeletonWidths';
import {
    fillerCellClassName,
    fullWidthCellClassName,
    rowClassName,
    rowStateClassNames,
} from './tableClasses';

/** The `cell` of columns that define none: their value as text. */
export function renderTextCell({ getValue }) {
    const value = getValue();
    return value == null ? null : String(value);
}

// A sticky cell must stay opaque over the rows scrolling beneath it, so a
// background from `meta.cellStyle` (usually a translucent heat-map tint) is
// laid over the row's background instead of replacing it.
function layerStickyTint(cellStyle) {
    const tint = cellStyle.backgroundColor ?? cellStyle.background;
    if (!tint) return cellStyle;
    const { background, backgroundColor, ...rest } = cellStyle;
    const image = String(tint).includes('gradient(') ? tint : `linear-gradient(${tint}, ${tint})`;
    return { ...rest, backgroundImage: image };
}

// One body cell from its TanStack cell: the column's `cell(context)` called
// as a plain render function (not mounted as a component, so the elements it
// returns keep their state when the column defs are rebuilt), with
// `meta.cellClassName` and `meta.cellStyle` applied. `stickyStyle` holds the
// offsets of a pinned or summary cell.
function renderBodyCell(entry, cell, className, stickyStyle) {
    const { cell: render, meta } = entry.column.columnDef;
    const context = cell.getContext();
    const cellClassName = typeof meta?.cellClassName === 'function'
        ? meta.cellClassName(context)
        : meta?.cellClassName;
    let cellStyle = meta?.cellStyle?.(context);
    if (cellStyle && stickyStyle) cellStyle = layerStickyTint(cellStyle);
    const style = stickyStyle || cellStyle ? { ...stickyStyle, ...cellStyle } : undefined;
    return (
        <td
            key={entry.id}
            data-field={entry.id}
            // Text cells get a native title when cut off (see DataTable).
            data-text-cell={render === renderTextCell ? '' : undefined}
            className={cx(className, cellClassName)}
            style={style}
        >
            {typeof render === 'function' ? render(context) : render}
        </td>
    );
}

function pinnedStyle(entry) {
    return entry.pinned ? { left: entry.stickyLeft } : undefined;
}

function shallowEqual(left, right) {
    if (left === right) return true;
    if (!left || !right) return false;
    const leftKeys = Object.keys(left);
    if (leftKeys.length !== Object.keys(right).length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
}

function rowPropsEqual(previous, next) {
    return previous.row === next.row
        && previous.layout === next.layout
        && previous.className === next.className
        && previous.ariaSelected === next.ariaSelected
        && previous.clickable === next.clickable
        && previous.checked === next.checked
        && previous.canSelect === next.canSelect
        && previous.fullWidthContent === next.fullWidthContent
        && previous.columnSpan === next.columnSpan
        && previous.dataIndex === next.dataIndex
        && previous.rowRef === next.rowRef
        && shallowEqual(previous.rowProps, next.rowProps);
}

// One body row. Memoized: a re-render of the table re-renders only rows
// whose row, columns, look or selection changed (and, since getRowProps
// usually returns fresh handlers, rows that have extra props). Clicks and
// keys are handled once on the <tbody> (see DataTable).
function DataTableRowComponent({
    row,
    layout,
    className,
    ariaSelected,
    clickable,
    checked,
    canSelect,
    fullWidthContent,
    columnSpan,
    rowProps,
    dataIndex,
    rowRef,
}) {
    const sharedProps = {
        ref: rowRef,
        'data-row-id': row.id,
        'data-index': dataIndex,
        tabIndex: clickable ? 0 : undefined,
        'aria-selected': ariaSelected,
        ...rowProps,
        className: cx(className, rowProps?.className),
    };
    if (fullWidthContent != null) {
        return (
            <tr {...sharedProps}>
                <td colSpan={columnSpan} className={fullWidthCellClassName}>
                    {fullWidthContent}
                </td>
            </tr>
        );
    }
    const cells = row.getAllCellsByColumnId();
    return (
        <tr {...sharedProps}>
            {layout.entries.map((entry) => {
                if (entry.id !== SELECT_COLUMN_ID) {
                    return renderBodyCell(entry, cells[entry.id], entry.bodyClassName, pinnedStyle(entry));
                }
                return (
                    <td key={entry.id} data-field={entry.id} className={entry.bodyClassName} style={pinnedStyle(entry)}>
                        <Checkbox
                            aria-label="Select row"
                            checked={checked}
                            disabled={!canSelect}
                            onChange={row.getToggleSelectedHandler()}
                        />
                    </td>
                );
            })}
            {layout.filler && <td aria-hidden="true" className={fillerCellClassName} />}
        </tr>
    );
}

DataTableRowComponent.propTypes = {
    row: PropTypes.object.isRequired,
    layout: PropTypes.object.isRequired,
    className: PropTypes.string,
    ariaSelected: PropTypes.bool,
    clickable: PropTypes.bool,
    checked: PropTypes.bool,
    canSelect: PropTypes.bool,
    fullWidthContent: PropTypes.node,
    columnSpan: PropTypes.number.isRequired,
    rowProps: PropTypes.object,
    dataIndex: PropTypes.number,
    rowRef: PropTypes.func,
};

export const DataTableRow = React.memo(DataTableRowComponent, rowPropsEqual);

/**
 * A summary row (plain row object built against the table, outside its row
 * model): the same cells, sticky at `stickyStyle` (a `top` or `bottom`
 * offset), with nothing in the checkbox column.
 */
export function SummaryRow({ row, layout, className, stickyStyle, kind }) {
    const cells = row.getAllCellsByColumnId();
    return (
        <tr data-summary-row={kind} className={className}>
            {layout.entries.map((entry) => {
                const style = { ...stickyStyle, ...pinnedStyle(entry) };
                if (entry.id === SELECT_COLUMN_ID) {
                    return <td key={entry.id} className={entry.summaryClassName} style={style} />;
                }
                return renderBodyCell(entry, cells[entry.id], entry.summaryClassName, style);
            })}
            {layout.filler && <td aria-hidden="true" className={fillerCellClassName} />}
        </tr>
    );
}

SummaryRow.propTypes = {
    row: PropTypes.object.isRequired,
    layout: PropTypes.object.isRequired,
    className: PropTypes.string,
    stickyStyle: PropTypes.object.isRequired,
    kind: PropTypes.oneOf(['top', 'bottom']).isRequired,
};

// A string `meta.cellClassName` is the column's own look (the optimized
// tint), so loading cells wear it too; a function needs a row.
function columnCellClassName(entry) {
    const cellClassName = entry.column.columnDef.meta?.cellClassName;
    return typeof cellClassName === 'string' ? cellClassName : undefined;
}

/**
 * The loading state: `rowCount` placeholder rows, striped like data rows
 * (without the hover), a Skeleton bar in every cell with a stable
 * pseudo-random width (40-80%), hidden from assistive technology (the table
 * carries aria-busy). Cells keep their column's string `meta.cellClassName`.
 */
export function SkeletonRows({ rowCount, layout }) {
    const nextSkeletonWidth = createSkeletonWidths();
    return Array.from({ length: rowCount }, (_, rowIndex) => (
        <tr
            key={rowIndex}
            aria-hidden="true"
            className={cx(rowClassName, rowIndex % 2 === 1 ? rowStateClassNames.plainOdd : rowStateClassNames.plain)}
        >
            {layout.entries.map((entry) => (
                <td
                    key={entry.id}
                    data-field={entry.id}
                    className={cx(entry.bodyClassName, columnCellClassName(entry))}
                    style={pinnedStyle(entry)}
                >
                    <Skeleton
                        width={nextSkeletonWidth()}
                        className={cx(entry.align === 'right' && 'ml-auto', entry.align === 'center' && 'mx-auto')}
                    />
                </td>
            ))}
            {layout.filler && <td className={fillerCellClassName} />}
        </tr>
    ));
}

// Decorative empty rows keep the real column layout and tints without creating
// selectable records, invoking cell renderers, or announcing a loading state.
export function EmptyRows({ rowCount, layout, striped = true }) {
    return Array.from({ length: rowCount }, (_, index) => (
        <tr key={index} aria-hidden="true" data-empty-row="true"
            className={cx(rowClassName, striped && index % 2 ? rowStateClassNames.plainOdd : rowStateClassNames.plain)}>
            {layout.entries.map((entry) => <td key={entry.id} data-field={entry.id}
                className={cx(entry.bodyClassName, columnCellClassName(entry))} style={pinnedStyle(entry)} />)}
            {layout.filler && <td className={fillerCellClassName} />}
        </tr>
    ));
}

SkeletonRows.propTypes = {
    rowCount: PropTypes.number.isRequired,
    layout: PropTypes.object.isRequired,
};
